import type { HttpTransport } from "@common/api/transport";
import type { ID, PaginatedResponse, PaginationParams } from "@common/types/api";
import type {
  DetailedInventoryItem,
  InventoryItem,
  InventoryUnit,
  ItemCategory,
  NewItemFormValues,
  TaskItem
} from "@common/types/Inventory";
import type { SpreadsheetImport } from "@common/types/SpreadsheetImport";
import type { TaskTypesWithNoneType } from "@common/types/TaskTypes";

export const createInventoryApi = (http: HttpTransport) => ({
  bulkDestroyInventoryItems: (ids: number[]) =>
    http.del<{ status: number }>("/api/inventory/items/bulk_destroy", { ids }),

  createInventoryItem: (params: NewItemFormValues) =>
    http.post<InventoryItem>("/api/inventory/items", { item: params }),

  createSpreadsheetImport: (params: { spreadsheet: File }) => {
    const formData = new FormData();
    formData.append("spreadsheet", params.spreadsheet);

    return http.upload<{ status: number }>("/api/inventory/imports", formData);
  },

  // jsonMutate returns { status: number } if not parseable to json (204 response)
  destroyInventoryItem: (id: number) =>
    http.del<{ status: number }>(`/api/inventory/items/${id}`, {}),

  fetchInventoryItem: (id: number) =>
    http.get<{ item: DetailedInventoryItem }>(`/api/inventory/items/${id}`),

  fetchInventoryItems: ({
    categoryIds,
    gardenIds,
    limit,
    orderBy,
    page,
    search,
    taskTypes
  }: {
    categoryIds?: number[];
    gardenIds?: ("none" | number)[];
    search?: string;
    page: number;
    limit: number;
    taskTypes?: TaskTypesWithNoneType;
    orderBy?: {
      column: string;
      order: "ascend" | "descend";
    };
  }) =>
    http.get<PaginatedResponse<InventoryItem>>("/api/inventory/items", {
      queryParams: {
        categoryIds,
        gardenIds,
        limit,
        orderBy,
        page,
        search,
        taskTypes
      }
    }),

  fetchInventoryUnits: () => http.get<{ units: InventoryUnit[] }>("/api/inventory/units"),

  fetchItemCategories: () =>
    http.get<{ categories: ItemCategory[] }>("/api/inventory/categories"),

  fetchOngoingImport: () =>
    http.get<{ ongoingImport: SpreadsheetImport | null }>(
      "/api/inventory/imports/ongoing"
    ),

  fetchSpreadsheetImports: ({ limit, page }: PaginationParams = {}) =>
    http.get<PaginatedResponse<SpreadsheetImport>>(`/api/inventory/imports`, {
      queryParams: { limit, page }
    }),

  fetchTaskItems: ({
    page,
    showOnlyTaskGardenItems,
    taskId
  }: {
    page: number;
    taskId: ID;
    showOnlyTaskGardenItems: boolean;
  }) =>
    http.get<PaginatedResponse<TaskItem>>(`/api/tasks/${taskId}/items`, {
      queryParams: { page, showOnlyTaskGardenItems }
    }),

  updateInventoryItem: (id: number, params: NewItemFormValues) =>
    http.put<InventoryItem>(`/api/inventory/items/${id}`, { item: params })
});
